import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  EmptyState, ListTable, MetricCard, Panel, SearchField, SegmentedControl, StatusChip, ToolbarButton,
  type ListColumn, type StatusTone,
} from '../../../ui-kit';
import { fetchCalls, type CallRow, type CallStatus, type CallsByDay } from '../../../services/roccoService';
import { lengthOf, matches, useLive, when } from './live';
import ListDownload from './ListDownload';
import AgentName from './AgentName';
import LeadStoryModal from './LeadStory';

type Filter = 'all' | 'callback' | 'connected' | 'missed';

const MATCHES: Record<Filter, (c: CallRow) => boolean> = {
  all: () => true,
  callback: (c) => c.status === 'callback',
  connected: (c) => c.status === 'connected',
  missed: (c) => c.status === 'no answer' || c.status === 'denied' || c.status === 'failed',
};

/** How a call reads, in a word or two, and the tone that goes with it. */
const HOW: Record<CallStatus, [StatusTone, string]> = {
  ringing: ['good', 'Ringing'],
  'in progress': ['good', 'On the call'],
  // The audio has stopped; the provider is still reading what was agreed.
  ended: ['neutral', 'Call ended · booking…'],
  callback: ['good', 'Callback booked'],
  connected: ['good', 'Contacted'],
  'no answer': ['neutral', 'No answer'],
  denied: ['bad', 'Declined'],
  failed: ['warning', "Didn't go through"],
};

/**
 * Every call the AI has made, newest first, as it happens.
 *
 * A call appears the moment it is dialled and changes in place — ringing, on
 * the call, ended, booked — so the page can be left open during a live call.
 * Opening one shows the transcript, and from there the lead's whole story.
 */
export default function ConversationsPage() {
  const { data, error, loading, reconnecting } = useLive(fetchCalls);
  const [filter, setFilter] = React.useState<Filter>('all');
  const [open, setOpen] = React.useState<{ lead: string; call: string } | null>(null);
  const [query, setQuery] = React.useState('');

  const calls = data?.calls ?? [];
  const rows = calls.filter(MATCHES[filter])
    .filter((c) => matches(query, [c.lead, c.agent, HOW[c.status][1], c.outcome]));
  const countOf = (f: Filter) => calls.filter(MATCHES[f]).length;
  const days = data?.by_day ?? [];

  const columns: ListColumn<CallRow>[] = [
    {
      key: 'time',
      sortValue: (c) => c.started_at, sortStartsDescending: true,
      header: 'Time',
      width: 130,
      render: (c) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}>
          {when(c.started_at)}
        </Typography>
      ),
    },
    {
      key: 'lead',
      sortValue: (c) => c.lead,
      header: 'Lead',
      grow: 1,
      minWidth: 120,
      render: (c) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{c.lead}</Typography>
      ),
    },
    {
      key: 'agent',
      sortValue: (c) => c.agent,
      header: 'Agent',
      width: 120,
      render: (c) => c.agent ? <AgentName person={{ name: c.agent, kind: c.agent_kind }} /> : null,
    },
    {
      key: 'how',
      sortValue: (c) => HOW[c.status][1],
      header: 'How it went',
      width: 180,
      render: (c) => <StatusChip tone={HOW[c.status][0]} label={HOW[c.status][1]} />,
    },
    {
      key: 'length',
      sortValue: (c) => (c.live ? null : c.duration_secs), sortStartsDescending: true,
      header: 'Length',
      width: 70,
      align: 'right',
      render: (c) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}>
          {c.live ? '—' : lengthOf(c.duration_secs)}
        </Typography>
      ),
    },
    {
      key: 'outcome',
      sortValue: (c) => c.outcome,
      header: 'Outcome',
      grow: 1.4,
      minWidth: 160,
      render: (c) => (
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>
          {c.outcome || (c.live ? 'In progress' : '—')}
        </Typography>
      ),
    },
    {
      key: 'open',
      header: '',
      width: 130,
      align: 'right',
      render: (c) => c.lead_id ? (
        <ToolbarButton variant="ghost" onClick={() => setOpen({ lead: c.lead_id!, call: c.id })}>
          View transcript
        </ToolbarButton>
      ) : null,
    },
  ];

  const table = (
    <ListTable
      columns={columns}
      rows={rows}
      getRowKey={(c) => c.id}
      pageSize={25}
      loading={loading}
      defaultSort={{ key: 'time', direction: 'desc' }}
      onRowClick={(c) => c.lead_id && setOpen({ lead: c.lead_id, call: c.id })}
      emptyMessage={calls.length
        ? 'Nothing matches. Clear the search or the filter.'
        : 'No calls yet. The first appears here the moment an AI agent dials.'}
    />
  );
  // Search beside the title, the switch on the left of the toolbar, the
  // download on its right (the panel header's two rows, 21 Oct 2026).
  const searchBox = <SearchField value={query} onChange={setQuery} placeholder="Search calls…" />;
  const picker = (
    <SegmentedControl
      value={filter}
      onChange={(next) => setFilter(next)}
      ariaLabel="Which calls"
      options={[
        { value: 'all', label: `All · ${countOf('all')}` },
        { value: 'callback', label: `Callbacks · ${countOf('callback')}` },
        { value: 'connected', label: `Contacted · ${countOf('connected')}` },
        { value: 'missed', label: `Missed · ${countOf('missed')}` },
      ]}
    />
  );
  const download = <ListDownload list="calls" total={calls.length} noun="calls" />;

  const tile = (key: keyof Omit<CallsByDay, 'day'>, label: string, info: string) => {
    const total = calls.filter((c) => {
      if (key === 'calls') return true;
      if (key === 'connected') return c.status === 'connected' || c.status === 'callback';
      if (key === 'callbacks') return c.status === 'callback';
      return c.agent_kind === 'ai';
    }).length;
    return (
      <MetricCard
        loading={loading}
        label={label}
        value={total.toLocaleString()}
        info={info}
        trend={{
          points: days.map((d, i) => ({
            key: d.day,
            label: i === days.length - 1 ? 'Today' : new Date(`${d.day}T12:00:00Z`)
              .toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }),
            value: d[key],
          })),
          formatValue: (v: number) => v.toLocaleString(),
          label: `${label} by day, last 7 days`,
        }}
      />
    );
  };

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
        {tile('calls', 'Calls', 'Every call the AI agents have placed, answered or not.')}
        {tile('connected', 'Contacted', 'Calls where the lead picked up and spoke.')}
        {tile('callbacks', 'Callbacks booked', 'Calls that ended with a time agreed, now with an advisor.')}
        {tile('by_ai', 'By AI', 'Calls an AI agent made without a person on the line.')}
      </Box>

      <Panel
        title="Conversations"
        subtitle={`${calls.length} calls · newest first · ${reconnecting ? 'Reconnecting…' : 'a call appears as it is dialled and updates as it goes'}`}
        search={searchBox}
        tools={picker}
        actions={download}
        expanded={{ content: table, actions: <>{searchBox}{picker}{download}</> }}
      >
        {error && !data ? <EmptyState message={error} /> : null}
        {error && !data ? null : table}
      </Panel>

      {open ? <LeadStoryModal leadId={open.lead} callId={open.call} onClose={() => setOpen(null)} /> : null}
    </Stack>
  );
}
